import { BarChart, TrendingUp } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Grid,
  PopperPlacementType,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { getCessionsSummary } from 'lib/api/dashboard';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import MiniPieChart from './components/miniPieChart';
import {
  BASE_SUMMARY,
  CessionsSummary,
  SelectedBusiness,
  SelectedFactoring,
  SelectedFactorings,
} from './types';

const MyWalletSummary = () => {
  const dispatch = useDispatch();
  const [cessionsSummary, setCessionsSummary] =
    useState<CessionsSummary>(BASE_SUMMARY);
  const [otherFactoringsSelected, setOtherFactoringsSelected] =
    useState<SelectedFactorings>([]);
  const [openBusinessPopper, setOpenBusinessPopper] = useState<boolean>(false);
  const [businessAnchorEl, setBusinessAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [businessPlacement, setBusinessPlacement] =
    useState<PopperPlacementType>('right');
  const [selectedBusinessDate, setSelectedBusinessDate] = useState<
    string | null
  >(null);
  const [selectedBusiness, setSelectedBusiness] =
    useState<SelectedBusiness | null>(null);

  const [openFactoringPopper, setOpenFactoringPopper] =
    useState<boolean>(false);
  const [factoringAnchorEl, setFactoringAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [factoringPlacement, setFactoringPlacement] =
    useState<PopperPlacementType>('right');
  const [selectedFactoringDate, setSelectedFactoringDate] = useState<
    string | null
  >(null);
  const [selectedFactoring, setSelectedFactoring] =
    useState<SelectedFactoring | null>(null);
  const [filteredFactoringTable, setFilteredFactoringTable] = useState<
    SelectedFactoring[]
  >([]);
  const [filteredBusinessTable, setFilteredBusinessTable] = useState<
    SelectedBusiness[]
  >([]);
  const [selectedBusinessFilter, setSelectedBusinessFilter] =
    useState<SelectedBusiness | null>(null);
  const [selectedFactoringFilter, setSelectedFactoringFilter] =
    useState<SelectedFactoring | null>(null);
  const [showAsPercentage, setShowAsPercentage] = useState(false);

  const { data, isPending, error } = getCessionsSummary(dispatch);

  const transformGraphDataToPercentage = (
    data: typeof cessionsSummary.graph,
  ) => {
    return data.map((item) => {
      return {
        name: item.name,
        'Participación empresa':
          item.Total > 0 ? (item['Total empresa'] / item.Total) * 100 : 0,
      };
    });
  };

  const graphData = useMemo(() => {
    if (showAsPercentage) {
      return transformGraphDataToPercentage(cessionsSummary.graph);
    }
    return cessionsSummary.graph;
  }, [cessionsSummary.graph, showAsPercentage]);

  const graphKey = `${showAsPercentage ? 'percentage' : 'millions'}-${graphData.length}`;

  const formatYAxis = (value: number) => {
    if (showAsPercentage) return `${value.toFixed(1)}%`;
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(0)}MM`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toString();
  };

  const handleBusinessSelectPercentage = (
    key: string,
    id: string,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const business = cessionsSummary.businessTable.find(
      (x) => x.businessIdentifier === id,
    );
    if (!business) return;
    const dateSelected = business.otherFactorings[key];
    if (!dateSelected)
      return alert('Este cliente no tuvo cesiones en este mes');

    const factoringsValues = Object.keys(dateSelected).map((x) => ({
      name: x,
      value: dateSelected[x],
    }));
    setOtherFactoringsSelected(factoringsValues);
    setSelectedBusiness(business);
    setSelectedBusinessDate(key === 'allYear' ? 'Año completo' : key);
    void handleClickBusinessPopper('right')(e);
  };

  const handleFactoringSelectPercentage = (
    key: string,
    id: string,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const factoring = cessionsSummary.factoringTable.find(
      (x) => x.cessionaryIdentifier === id,
    );
    if (!factoring) return;
    const dateSelected = factoring.businesses[key];
    if (!dateSelected)
      return alert('Este factoring no tuvo cesiones en este mes');

    const businessValues = Object.keys(dateSelected).map((x) => ({
      name: x,
      value: dateSelected[x],
    }));

    setOtherFactoringsSelected(businessValues);
    setSelectedFactoring(factoring);
    setSelectedFactoringDate(key === 'allYear' ? 'Año completo' : key);
    void handleClickFactoringPopper('right')(e);
  };

  const handleClickBusinessPopper =
    (newPlacement: PopperPlacementType) =>
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      setBusinessAnchorEl(event.currentTarget);
      setOpenBusinessPopper(
        (prev) => businessPlacement !== newPlacement || !prev,
      );
      setBusinessPlacement(newPlacement);
    };

  const handleClickFactoringPopper =
    (newPlacement: PopperPlacementType) =>
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      setFactoringAnchorEl(event.currentTarget);
      setOpenFactoringPopper(
        (prev) => factoringPlacement !== newPlacement || !prev,
      );
      setFactoringPlacement(newPlacement);
    };

  const handleChangeBusiness = (newValue: SelectedBusiness | null) => {
    if (!newValue) {
      setSelectedBusinessFilter(null);
      return setFilteredBusinessTable(cessionsSummary.businessTable);
    }
    const filtered = cessionsSummary.businessTable.filter(
      (x) => x.businessIdentifier === newValue.businessIdentifier,
    );
    setFilteredBusinessTable(filtered);
    setSelectedBusinessFilter(newValue);
    return;
  };

  const AutoCompleteBusiness = () => (
    <Autocomplete
      style={{ width: '300px' }}
      options={cessionsSummary.businessTable}
      value={selectedBusinessFilter}
      getOptionLabel={(option) =>
        `${option.businessName} (${option.businessIdentifier})`
      }
      renderInput={(params) => (
        <TextField style={{ minWidth: 100 }} {...params} label="Cliente" />
      )}
      onChange={(_, newValue) => handleChangeBusiness(newValue)}
      fullWidth
    />
  );

  const handleChangeFactoring = (newValue: SelectedFactoring | null) => {
    if (!newValue) {
      setSelectedFactoringFilter(null);
      return setFilteredFactoringTable(cessionsSummary.factoringTable);
    }
    const filtered = cessionsSummary.factoringTable.filter(
      (x) => x.cessionaryIdentifier === newValue.cessionaryIdentifier,
    );
    setSelectedFactoringFilter(newValue);
    setFilteredFactoringTable(filtered);
    return;
  };

  const AutoCompleteFactoring = () => (
    <Autocomplete
      style={{ width: '300px' }}
      value={selectedFactoringFilter}
      options={cessionsSummary.factoringTable}
      getOptionLabel={(option) => `${option.cessionaryName}`}
      renderInput={(params) => (
        <TextField style={{ minWidth: 100 }} {...params} label="Factoring" />
      )}
      onChange={(_, newValue) => handleChangeFactoring(newValue)}
      fullWidth
    />
  );

  const clientTableHeaders = [
    { key: 'businessName', label: 'Cliente' },
    {
      key: 'allYear',
      label: 'Año completo',
      type: 'percentage',
      onClick: handleBusinessSelectPercentage,
    },
    ...(cessionsSummary.last6Months.map((x) => ({
      key: x,
      label: x,
      type: 'percentage',
      onClick: handleBusinessSelectPercentage,
    })) || []),
  ];

  const factoringTableHeaders = [
    { key: 'cessionaryName', label: 'Factoring' },
    {
      key: 'allYear',
      label: 'Año completo',
      type: 'percentage',
      onClick: handleFactoringSelectPercentage,
    },
    ...(cessionsSummary.last6Months.map((x) => ({
      key: x,
      label: x,
      type: 'percentage',
      onClick: handleFactoringSelectPercentage,
    })) || []),
  ];

  useEffect(() => {
    if (data) {
      setFilteredBusinessTable(data.businessTable);
      setFilteredFactoringTable(data.factoringTable);
      setCessionsSummary(data);
    }
    if (error) errorHandler(dispatch, error);
  }, [data, error]);

  if (isPending) return <CenteredCircularProgress type="layout" />;

  return (
    <Box marginTop={2}>
      <Title
        title="¿Cómo vamos?"
        icon={<TrendingUp color="primary" sx={{ fontSize: 20 }} />}
        color="primary"
      />
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <TableCard>
            <Box position="relative">
              <Box position="absolute" top={8} right={8} zIndex={1}>
                <ToggleButtonGroup
                  value={showAsPercentage ? 'percentage' : 'millions'}
                  exclusive
                  onChange={(_, value) => {
                    if (value !== null) {
                      setShowAsPercentage(value === 'percentage');
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      minWidth: '120px',
                    },
                  }}
                >
                  <ToggleButton value="millions" aria-label="millions">
                    Monto
                  </ToggleButton>
                  <ToggleButton value="percentage" aria-label="percentage">
                    Porcentajes
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <LineChart
                key={graphKey}
                data={graphData}
                showLegend={true}
                formatYAxis={formatYAxis}
                height={320}
              />
            </Box>
          </TableCard>
        </Grid>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Este mes"
              tooltipContent="Aca podras ver el resumen de las cesiones de tu factoring en el último mes."
              tooltipPlacement="right"
              variant="compact"
            ></SubtitleWithTooltip>
            <CentralPieChart
              data={cessionsSummary.summary.lastMonth}
              showLegend={true}
            />
          </TableCard>
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <Grid item xs={12}>
          <Title
            title="Penetración de mercado"
            icon={<BarChart color="primary" sx={{ fontSize: 20 }} />}
            color="primary"
          />
          <TableCard>
            <SubtitleWithTooltip
              title="Por clientes"
              tooltipContent="Estos son los porcentajes de penetración de tu factoring en cada cliente. Si aprietas en un porcentaje, podrás ver la distribución."
              tooltipPlacement="right"
              variant="compact"
            >
              <AutoCompleteBusiness />
            </SubtitleWithTooltip>

            <EnhancedTable
              data={filteredBusinessTable}
              dataHeaders={clientTableHeaders}
              finalSumRow
              exportToFile="xlsx"
            />
          </TableCard>
        </Grid>
        <Grid item xs={12}>
          <TableCard>
            <SubtitleWithTooltip
              title="Por factorings"
              tooltipContent="Estos son los porcentajes de penetración de otros factorings en tus cesiones perdidas. Si aprietas un porcentaje, podrás ver la distribución."
              tooltipPlacement="right"
              variant="compact"
            >
              <AutoCompleteFactoring />
            </SubtitleWithTooltip>
            <EnhancedTable
              data={filteredFactoringTable}
              dataHeaders={factoringTableHeaders}
              finalSumRow
              exportToFile="xlsx"
            />
          </TableCard>
        </Grid>
      </Grid>
      <MiniPieChart
        data={otherFactoringsSelected}
        openPopper={openBusinessPopper}
        setOpenPopper={setOpenBusinessPopper}
        anchorEl={businessAnchorEl}
        setAnchorEl={setBusinessAnchorEl}
        placement={businessPlacement}
        handleClickPopper={handleClickBusinessPopper}
        date={selectedBusinessDate}
        name={selectedBusiness?.businessName}
      />
      <MiniPieChart
        data={otherFactoringsSelected}
        openPopper={openFactoringPopper}
        setOpenPopper={setOpenFactoringPopper}
        anchorEl={factoringAnchorEl}
        setAnchorEl={setFactoringAnchorEl}
        placement={factoringPlacement}
        handleClickPopper={handleClickFactoringPopper}
        date={selectedFactoringDate}
        name={selectedFactoring?.cessionaryName}
      />
    </Box>
  );
};

export default MyWalletSummary;
